import { Component, ViewChild, ElementRef, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SidebarModule } from 'primeng/sidebar';
import { SIDEBAR_CONFIG } from './sidebar-config';

export const SIDEBAR_TIMINGS = {
  OVERLAY_ANIMATION_MS: 300,
  MODE_SWITCH_DELAY_MS: 150,
  FADE_IN_DELAY_MS: 100,
  RETRY_INTERVAL_MS: 50,
  FADE_IN_TRIGGER_MS: 50
} as const;

@Component({
  selector: 'app-dynamic-sidebar',
  standalone: true,
  imports: [CommonModule, SidebarModule],
  template: `
    <p-sidebar
        [(visible)]="visible"
        position="right"
        [modal]="false"
        [closeOnEscape]="false"
        [style]="{ width: config.overlayWidth + 'px' }"
        (onHide)="handleHide()">
      <ng-template pTemplate="headless">
        <div #contentContainer class="sidebar-content-wrapper" [class.fade-in]="contentLoaded">
        </div>
      </ng-template>
    </p-sidebar>
  `,
  styles: [`
    :host ::ng-deep .p-sidebar {
      background: #F9F9F9;
      box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
    }
    :host ::ng-deep .p-sidebar-content {
      padding: 1.5rem;
      background: white;
      color: #000;
      font-size: 0.875rem;
    }
    :host ::ng-deep .p-sidebar-content p {
      margin: 0;
      line-height: 1.5;
    }
    .sidebar-content-wrapper {
      opacity: 0;
      transition: opacity 150ms ease-out;
      height: 100%;
    }
    .sidebar-content-wrapper.fade-in {
      opacity: 1;
    }
  `]
})
export class DynamicSidebarComponent implements OnDestroy {
  readonly config = inject(SIDEBAR_CONFIG);

  visible = false;
  contentLoaded = false;
  suppressCloseEvent = false;

  @ViewChild('contentContainer')
  contentContainer!: ElementRef<HTMLElement>;

  private attachedContent: HTMLElement | null = null;

  onClose = () => {};

  handleHide(): void {
    if (!this.suppressCloseEvent) {
      this.onClose();
    }
    this.suppressCloseEvent = false;
  }

  closeWithoutCallback(): void {
    this.suppressCloseEvent = true;
    this.visible = false;
  }

  open(): void {
    this.visible = true;
    setTimeout(() => {
      this.contentLoaded = true;
    }, SIDEBAR_TIMINGS.FADE_IN_TRIGGER_MS);
  }

  attachContent(contentElement: HTMLElement): void {
    this.attachedContent = contentElement;
    this.contentLoaded = false;

    if (this.contentContainer?.nativeElement && contentElement) {
      if (contentElement.parentNode) {
        contentElement.parentNode.removeChild(contentElement);
      }
      this.contentContainer.nativeElement.appendChild(contentElement);
      setTimeout(() => {
        this.contentLoaded = true;
      }, SIDEBAR_TIMINGS.FADE_IN_TRIGGER_MS);
    } else {
      setTimeout(() => {
        if (this.contentContainer?.nativeElement && this.attachedContent) {
          if (this.attachedContent.parentNode) {
            this.attachedContent.parentNode.removeChild(this.attachedContent);
          }
          this.contentContainer.nativeElement.appendChild(this.attachedContent);
          setTimeout(() => {
            this.contentLoaded = true;
          }, SIDEBAR_TIMINGS.FADE_IN_TRIGGER_MS);
        }
      }, SIDEBAR_TIMINGS.FADE_IN_DELAY_MS);
    }
  }

  detachContent(): void {
    const content = this.attachedContent;
    this.attachedContent = null;
    this.contentLoaded = false;

    if (content && this.contentContainer?.nativeElement) {
      if (content.parentNode === this.contentContainer.nativeElement) {
        this.contentContainer.nativeElement.removeChild(content);
      }
    }
  }

  ngOnDestroy(): void {
    this.attachedContent = null;
  }
}
